import { Container } from 'pixi.js';
import { Confetti } from '../board/Confetti';
import { TitlePlaque } from '../board/TitlePlaque';
import { animate, ease, prefersReducedMotion } from '../board/ui';

/**
 * A level reached, on a carved plate that drops in under the top bar with
 * a burst of confetti, holds a moment and lifts away (it replaces the
 * purple "LEVEL N" pill that sat over the slot). Taps pass through.
 */
export class LevelUpToast extends Container {
  private readonly plate = new TitlePlaque('LEVEL 1', 24);
  private readonly confetti = new Confetti();
  private viewW = 1;
  private top = 70;
  private queue: Promise<void> = Promise.resolve();

  constructor() {
    super();
    this.eventMode = 'none';
    this.addChild(this.confetti, this.plate);
    this.visible = false;
  }

  /** The plate's text while it shows, for the browser tests. */
  get snapshot(): { visible: boolean; text: string } {
    return { visible: this.visible, text: this.plate.text };
  }

  /** `top`: where the plate sits, just under the top bar. */
  layout(viewWidth: number, top: number): void {
    this.viewW = viewWidth;
    this.top = top;
  }

  /** Show `level`; level-ups that come close together take turns. */
  show(level: number): Promise<void> {
    this.queue = this.queue.then(() => this.run(level));
    return this.queue;
  }

  private async run(level: number): Promise<void> {
    if (this.destroyed) return;
    this.plate.setTitle(`LEVEL ${level}!`);
    const y = this.top + this.plate.plateHeight / 2;
    this.plate.position.set(this.viewW / 2, y);
    this.visible = true;
    this.alpha = 1;
    if (prefersReducedMotion()) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } else {
      await animate(320, (t) => {
        this.plate.y = y - 40 * (1 - ease.outBack(t));
        this.plate.scale.set(0.7 + 0.3 * ease.outBack(t));
        this.alpha = Math.min(1, t * 2);
      });
      void this.confetti.burst(this.viewW / 2, y, { count: 60, spread: 320 });
      await new Promise((resolve) => setTimeout(resolve, 1700));
      await animate(260, (t) => {
        this.plate.y = y - 30 * ease.inCubic(t);
        this.alpha = 1 - t;
      });
    }
    if (!this.destroyed) this.visible = false;
  }
}
